import { useEffect, useState } from 'react';
import { ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from 'lucide-react';
import { Select } from './UI';

// wiki/proposals/pagination.md — a nézet-választó betöltéskor 100-ra áll; a
// backend csak ezt a számot + a "year" literált fogadja el (_pagination.py).
// ⚠ 2026-08-24: az 1000/oldal opció TÖRÖLVE — Gábor kérése ("sokkal
// egyszerűbb most"), mielőtt a mérési kapu (valós 1000-soros render/
// interakció-mérés) egyáltalán megtörtént volna.
export const PAGE_SIZE_OPTIONS = [
  { value: '100', label: '100 / oldal' },
  { value: 'year', label: 'Évente' },
];

/* Kicsi, keret nélküli ikon-gomb a lapozóhoz.
 *
 * ⚠ 2026-08-23: az eredeti verzió a megosztott `Btn`-t használta `outline`
 * variánssal + explicit 32×32-es hit-boxszal — Gábor élesben ezt "ormótlan"-nak
 * mondta: 4 db láthatóan kereteses doboz egymás mellett, egy amúgy kompakt
 * (0.72rem-es, kereteletlen) szűrő-sor fölött/alatt. A `Btn` fix méret-
 * készlete nem ad "csak hoverre anyagosodó" ikon-gombot, ezért ez egy önálló,
 * lokális komponens — NEM globális Btn-variáns, mert máshol nem kell.
 * Alapból teljesen sík (nincs háttér, nincs látható keret), csak hoverre jön
 * elő egy halvány `--bg-hover` folt — ez adja a visszafogott hatást. */
function NavIconBtn({ icon: Icon, disabled, onClick, label }) {
  return (
    <button
      type="button" onClick={onClick} disabled={disabled}
      aria-label={label} title={label}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 26, height: 26, padding: 0,
        background: 'transparent', border: 'none', borderRadius: 6,
        color: disabled ? 'var(--text-dim)' : 'var(--text-muted)',
        opacity: disabled ? 0.35 : 1,
        cursor: disabled ? 'default' : 'pointer',
        transition: 'background 0.12s, color 0.12s',
      }}
      onMouseEnter={e => { if (!disabled) {
        e.currentTarget.style.background = 'var(--bg-hover)';
        e.currentTarget.style.color = 'var(--text)';
      } }}
      onMouseLeave={e => {
        e.currentTarget.style.background = 'transparent';
        e.currentTarget.style.color = disabled ? 'var(--text-dim)' : 'var(--text-muted)';
      }}>
      <Icon size={14} />
    </button>
  );
}

/**
 * Általános lapozó sáv (Bizonylatok + Kivonatok) — lásd wiki/proposals/pagination.md.
 *
 * A hívó ezt a komponenst KÉTSZER rendereli — egyszer a tábla fölött jobbra
 * igazítva (`showViewSelector=true`), egyszer alatta (`showViewSelector=false`)
 * — hogy a nézet-választó Select csak EGY helyen éljen.
 *
 * ⚠ 2026-08-23, Gábor döntése: a pozíció-kijelzés egy beírható oldalszám-mező
 * ("[ 3 ] / 14"), NEM egy kattintható [1][2][3]…[14] gombsor. Ugyanaznap: a
 * tétel-tartomány felirat ("301–400 / 1281 tétel") TÖRÖLVE — élesben
 * kipróbálva feleslegesnek ítélte; a "[3] / 14" már megadja a pozíciót és a
 * terjedelmet is, pótlás nélkül.
 */
export function Pagination({
  mode,                 // 'count' | 'year'
  page,
  totalPages,
  pageLabel,            // pl. "2025" (year mód), egyébként null
  onPageChange,
  viewMode,             // '100' | 'year' — a page-size Select értéke
  onViewModeChange,
  showViewSelector = false,
}) {
  const [jumpValue, setJumpValue] = useState(String(page));
  useEffect(() => { setJumpValue(String(page)); }, [page]);

  const atFirst = page <= 1;
  const atLast = totalPages === 0 || page >= totalPages;
  const safeTotalPages = Math.max(totalPages, 1);

  const commitJump = () => {
    const n = parseInt(jumpValue, 10);
    if (Number.isFinite(n)) onPageChange(Math.min(Math.max(n, 1), safeTotalPages));
    else setJumpValue(String(page));
  };

  const firstLabel = mode === 'year' ? 'Legrégebbi év' : 'Első oldal';
  const prevLabel  = mode === 'year' ? 'Előző év' : 'Előző oldal';
  const nextLabel  = mode === 'year' ? 'Következő év' : 'Következő oldal';
  const lastLabel  = mode === 'year' ? 'Legfrissebb év' : 'Utolsó oldal';

  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      fontSize: '0.72rem', color: 'var(--text-muted)',
    }}>
      {showViewSelector && (
        <Select value={viewMode} onChange={e => onViewModeChange(e.target.value)}
          options={PAGE_SIZE_OPTIONS} aria-label="Nézet"
          style={{ fontSize: '0.72rem', padding: '0.15rem 0.4rem', width: 'auto', height: 26 }} />
      )}

      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
        <NavIconBtn icon={ChevronsLeft} disabled={atFirst} label={firstLabel}
          onClick={() => onPageChange(1)} />
        <NavIconBtn icon={ChevronLeft} disabled={atFirst} label={prevLabel}
          onClick={() => onPageChange(Math.max(1, page - 1))} />

        {mode === 'year' ? (
          <span style={{
            fontFamily: 'var(--mono)', fontWeight: 600, color: 'var(--text)',
            background: 'var(--bg-hover)', borderRadius: 999,
            padding: '2px 10px', margin: '0 2px',
          }}>{pageLabel ?? '–'}</span>
        ) : (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, margin: '0 2px' }}>
            <input
              type="text" inputMode="numeric" pattern="[0-9]*"
              value={jumpValue} aria-label="Oldal száma"
              onChange={e => setJumpValue(e.target.value.replace(/\D/g, ''))}
              onBlur={commitJump}
              onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
              style={{
                width: 30, height: 26, textAlign: 'center', background: 'var(--bg-input)',
                border: '1px solid var(--border)', borderRadius: 6,
                color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: '0.72rem', padding: 0,
              }} />
            <span style={{ fontFamily: 'var(--mono)', color: 'var(--text-dim)' }}>/ {safeTotalPages}</span>
          </span>
        )}

        <NavIconBtn icon={ChevronRight} disabled={atLast} label={nextLabel}
          onClick={() => onPageChange(page + 1)} />
        <NavIconBtn icon={ChevronsRight} disabled={atLast} label={lastLabel}
          onClick={() => onPageChange(totalPages)} />
      </div>
    </div>
  );
}
